import { useContext, useEffect, useState } from "react";
import {
  Button, FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextareaAutosize,
  TextField
} from "@mui/material";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "./AuthContext";
import Loader from "../../component/Loader";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import userImg from '../../assets/images/user.png';
import ReCAPTCHA from "react-google-recaptcha";
import { AuthAPI } from "../../api/AuthAPI";
import { UserAPI } from "../../api/UserAPI";
import LazyPhoneInput from "../../component/LazyPhoneInput";
import 'react-phone-input-2/lib/material.css';
import { GameAPI } from "../../api/GameAPI";
import * as Yup from 'yup';
import { useDispatch } from "react-redux";
import { rootAction } from "../../redux/reducer/rootReducer";
import {
  checkEmailAvailable,
  clubRegistrationFailureMessage,
  PHONE_VERIFICATION_REQUIRED,
  rateLimitFailureMessage,
} from "./emailVerification";
import {
  buildClubRegistrationPayload,
  extractPhoneVerificationToken,
  isValidUsMobile,
  toE164UsMobile,
} from "./clubOwnerRegistration";
import EmailTakenHint from "./EmailTakenHint";


const validatorObj = Yup.object({
  organizationName: Yup.string().max(100, "must not exceed 100 characters").required("Organization name is required"),
  firstName: Yup.string().max(100, "must not exceed 100 characters").required("First name is required"),
  lastName: Yup.string().max(100, "must not exceed 100 characters").required("Organization name is required"),
  email: Yup.string().max(100, "must not exceed 100 characters").required("Email is required"),
  phone: Yup.string()
    .required("Phone is required")
    .test("us-mobile", "Enter a valid US phone number", (value) => isValidUsMobile(value)),
  password: Yup.string()
    .required("Password is required")
    .min(8)
    .matches(/[A-Z]/, { excludeEmptyString: true })
    .matches(/[0-9]/, { excludeEmptyString: true })
    .matches(/^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@$*])[A-Za-z0-9!@$*]{8,20}$/, { excludeEmptyString: true })
  ,
  confirmPassword: Yup.string()
    .required("Confirm password is required")
    .oneOf([Yup.ref("password")], "Password must match"),
 // dob: Yup.string().required("Date of birth is required"),
  game: Yup.string().required("Game is required"),
  //address: Yup.string().max(100, "must not exceed 100 characters").required("Address is required"),
  description: Yup.string().max(1000, "must not exceed 1000 characters").required("Description is required"),
})

const ClubRegister = () => {
  document.title = "Club Registration"
  const navigate = useNavigate();
  const { setIsLoading, isLoading } = useContext(AuthContext);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [registerData, setRegisterData] = useState({});
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [isPhoneInputFocused, setIsPhoneInputFocused] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [phoneVerificationToken, setPhoneVerificationToken] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [passwordValidate, setPasswordValidate] = useState({ length: false, specialSymbal: false, allowedChars:false, number: false, upperLetter: false, lowerLetter:false })
  const [gameNames, setGameNames] = useState([]);
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const handleClickShowPassword = () => setShowPassword1((show) => !show);
  const handleClickShowConfirmPassword = () => setShowPassword2((show) => !show);

  const getAllGameNames = async () => {
    try {
      const response = await GameAPI.getGamesByPlatform();
      setGameNames(response);
    } catch (error) {

    }
  }

  useEffect(() => {
    if (gameNames.length < 1) {
      getAllGameNames();
    }
  }, [])
  const passwordValidator = (event) => {
    const { name, value } = event.target;
    const uppercasePattern = /[A-Z]/;
    const lowercasePattern = /[a-z]/;
    const numberPattern    = /[0-9]/;
    // Exactly one of ! @ $ *
    const specialCharPattern = /[!@$*]/; 
    const allowedCharsPattern = /^[A-Za-z0-9!@$*]+$/;

    let template = {}
    if (uppercasePattern.test(value)) {
      template.upperLetter = true;
    }
    if (lowercasePattern.test(value)) {
      template.lowerLetter = true;
    }
    if (specialCharPattern.test(value)) {
      template.specialSymbal = true;
    }
    if (allowedCharsPattern.test(value)) {
      template.allowedChars = true;
    }
    if (numberPattern.test(value)) {
      template.number = true;
    }
    if (value?.length >= 8 && value?.length <= 20) {
      template.length = true;
    }
    setPasswordValidate(template);

  };

  const applyEmailCheck = async (email) => {
    try {
      const result = await checkEmailAvailable(email, UserAPI.emailExists);
      if (!result.ok) {
        setErrors((prev) => ({ ...prev, email: result.message }));
        return false;
      }
      return true;
    } catch (error) {
      console.error(error);
      return true;
    }
  };

  const handleRegistrationData = (event) => {
    Object.keys(errors).length != 0 && setErrors({});
    const { name, value } = event.target;
    if (name === 'password') {
      passwordValidator(event);
    }
     const newVal = value;
    if (name === "phone") {
      setOtpSent(false);
      setPhoneVerified(false);
      setPhoneVerificationToken("");
      setOtpCode("");
    }
    setRegisterData(prev => ({ ...prev, [name]: newVal }));
  };

  const showApiError = (err, fallback) => {
    const content = clubRegistrationFailureMessage(err) || fallback;
    if (content) {
      dispatch(rootAction.setWarningMessage({ id: Date.now(), type: "error", content }));
    }
    return content;
  };

  const sendPhoneCode = async () => {
    const mobile = toE164UsMobile(registerData.phone);
    if (!mobile) {
      setErrors((prev) => ({ ...prev, phone: "Enter a valid US phone number" }));
      return;
    }
    try {
      setOtpBusy(true);
      await UserAPI.sendPhoneOtp(mobile);
      setOtpSent(true);
      setPhoneVerified(false);
      setPhoneVerificationToken("");
      setErrors((prev) => ({ ...prev, phone: undefined, otp: undefined }));
    } catch (err) {
      const limited = rateLimitFailureMessage(err);
      const content = limited || clubRegistrationFailureMessage(err) || "Could not send a code";
      setErrors((prev) => ({ ...prev, phone: content }));
      dispatch(rootAction.setWarningMessage({ id: Date.now(), type: "error", content }));
    } finally {
      setOtpBusy(false);
    }
  };

  const verifyPhoneCode = async () => {
    const mobile = toE164UsMobile(registerData.phone);
    const code = String(otpCode ?? "").trim();
    if (!mobile) {
      setErrors((prev) => ({ ...prev, phone: "Enter a valid US phone number" }));
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setErrors((prev) => ({ ...prev, otp: "Enter the 6-digit code" }));
      return;
    }
    try {
      setOtpBusy(true);
      const response = await UserAPI.verifyPhoneOtp(mobile, code);
      const token = extractPhoneVerificationToken(response);
      if (!token) {
        setErrors((prev) => ({ ...prev, otp: PHONE_VERIFICATION_REQUIRED }));
        return;
      }
      setPhoneVerificationToken(token);
      setPhoneVerified(true);
      setErrors((prev) => ({ ...prev, otp: undefined }));
    } catch (err) {
      const limited = rateLimitFailureMessage(err);
      const content = limited || clubRegistrationFailureMessage(err) || "Invalid or expired code";
      setPhoneVerified(false);
      setPhoneVerificationToken("");
      setErrors((prev) => ({ ...prev, otp: content }));
      dispatch(rootAction.setWarningMessage({ id: Date.now(), type: "error", content }));
    } finally {
      setOtpBusy(false);
    }
  };

  const submitRegisterData = async (event) => {
    try {
      event.preventDefault();
      await validatorObj.validate(registerData, { abortEarly: false });
      if (!(await applyEmailCheck(registerData.email))) {
        return;
      }
      if (!phoneVerificationToken) {
        setErrors((prev) => ({ ...prev, otp: PHONE_VERIFICATION_REQUIRED }));
        dispatch(rootAction.setWarningMessage({
          id: Date.now(),
          type: "error",
          content: PHONE_VERIFICATION_REQUIRED,
        }));
        return;
      }
      const clubRegistrationData = buildClubRegistrationPayload(
        registerData,
        phoneVerificationToken,
      );

      setIsLoading(true);
      await UserAPI.registerUser(clubRegistrationData);
      navigate("/email-sent", {
        replace: true,
        state: { userName: registerData.email, registerFor: 'club' },
      });
      setIsLoading(false);
    } catch (err) {
      if (err.inner) {
        let errObj = {};
        err.inner.forEach(e => {
          errObj[e.path] = e.message
        });
        setErrors(errObj);
      } else {
        const content = showApiError(err, PHONE_VERIFICATION_REQUIRED);
        if (content === PHONE_VERIFICATION_REQUIRED) {
          setErrors((prev) => ({ ...prev, otp: content }));
        }
        if (err?.response?.status === 409) {
          setErrors((prev) => ({ ...prev, email: content }));
        }
        console.error(err);
      }

    } finally {
      setIsLoading(false);
    }


  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event) => {
    event.preventDefault();
  };
  const verifyCaptcha = async (data) => {
    try {
      const response = await AuthAPI.postVerifyCaptcha(data);
      setCaptchaVerified(response.Data);
    } catch (error) {

    }
  }

  return (
    <div className="player_register_container" onSubmit={submitRegisterData}>
      {isLoading && <Loader />}
      <form className="player_register_container_body">
        <div className="player_heading">
          <img src={userImg} alt="" />
          <h2>Club Registration</h2>
        </div>
        <TextField
          inputProps={{ maxLength: 100 }}
          error={errors['organizationName']}
          helperText={errors['organizationName'] && errors['organizationName']}
          value={registerData.organizationName || ""}
          onChange={handleRegistrationData}
          name="organizationName"
          className="player-reg-text-input"
          label="Organization Name"
          variant="outlined"
        />
        <div className="player_name">
          <TextField
            inputProps={{ maxLength: 100 }}
            error={errors['firstName']}
            helperText={errors['firstName'] && errors['firstName']}
            value={registerData.firstName || ""}
            onChange={handleRegistrationData}
            name="firstName"
            className="player-reg-text-input"
            sx={{ width: "49%", "@media (max-width:400px)": { width: "100%" } }}
            label="First Name"
            variant="outlined"
          />

          <TextField
            inputProps={{ maxLength: 100 }}
            error={errors['lastName']}
            helperText={errors['lastName'] && errors['lastName']}
            value={registerData.lastName || ""}
            onChange={handleRegistrationData}
            name="lastName"
            className="player-reg-text-input"
            sx={{ width: "49%", "@media (max-width:400px)": { width: "100%" } }}
            label="Last Name"
            variant="outlined"
          />
        </div>
        <TextField
          inputProps={{ maxLength: 100 }}
          error={Boolean(errors['email'])}
          helperText={errors['email'] ? <EmailTakenHint message={errors['email']} /> : null}
          value={registerData.email || ""}
          onChange={handleRegistrationData}
          onBlur={() => registerData.email && applyEmailCheck(registerData.email)}
          name="email"
          className="player-reg-text-input"
          type="email"
          label="Email"
          variant="outlined"
        />

        <div className="club-register-phone-block">
        <label className="club-register-phone-label" htmlFor="club-register-phone">US mobile</label>
        <p className="club-register-phone-hint">We text a 6-digit code to this number. Verify it before you create the club.</p>
        <LazyPhoneInput
          country={'us'}
          onlyCountries={['us']}
          disableDropdown={true}
          countryCodeEditable={false}
          inputProps={{ id: "club-register-phone", name: "phone" }}
          specialLabel="US mobile"
          aria-label="Phone"
          containerStyle={isPhoneInputFocused ?
            errors['phone'] ?
              {
                color: '#d32f2f'
              }
              :
              { color: 'var(--hoverColorBold)' }
            :
            errors['phone'] ?
              {
                color: '#d32f2f'
              } : { color: 'black' }}
          inputStyle={isPhoneInputFocused ?
            errors['phone'] ?
              {
                border: '1px solid #d32f2f',
                boxShadow: 'none',
                width: '100%',
                color: '#d32f2f',
                borderRadius: '10px',
              } :
              {
                border: '2px solid var(--hoverColorBold)',
                boxShadow: 'none',
                transition: '.2s ease',
                borderRadius: '10px',
                width: '100%'
              } : errors['phone'] ?
              {
                border: '1px solid #d32f2f',
                boxShadow: 'none',
                width: '100%',
                color: '#d32f2f',
                borderRadius: '10px',
              } : { borderRadius: '10px', width: '100%' }}
          value={registerData.phone || ''}
          onFocus={() => setIsPhoneInputFocused(true)}
          onBlur={() => setIsPhoneInputFocused(false)}
          onChange={(e) => handleRegistrationData({ target: { name: 'phone', value: e } })}
        />
        {errors['phone'] && <div className="phone-number-input-error-msg">{errors['phone']}</div>}
        <div className="club-register-phone-otp">
          <p className="club-register-otp-label" id="club-register-send-code-hint">Phone verification — we text a 6-digit code</p>
          <div className="club-register-phone-otp-row">
            <Button
              type="button"
              size="small"
              variant="outlined"
              aria-describedby="club-register-send-code-hint"
              disabled={!isValidUsMobile(registerData.phone) || otpBusy || phoneVerified}
              onClick={sendPhoneCode}
            >
              {phoneVerified ? "Verified" : otpSent ? "Resend" : "Send code"}
            </Button>
            {otpSent && !phoneVerified && (
              <>
                <TextField
                  inputProps={{ maxLength: 6, inputMode: "numeric", autoComplete: "one-time-code" }}
                  error={Boolean(errors.otp)}
                  helperText={errors.otp}
                  value={otpCode}
                  onChange={(event) => setOtpCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  name="otp"
                  className="club-register-otp-input"
                  label="6-digit code"
                  variant="outlined"
                  size="small"
                />
                <Button
                  type="button"
                  size="small"
                  variant="contained"
                  disabled={otpCode.length !== 6 || otpBusy}
                  onClick={verifyPhoneCode}
                >
                  Verify
                </Button>
              </>
            )}
          </div>
          {phoneVerified && (
            <p className="club-register-phone-verified">US mobile verified. Token is valid for 15 minutes.</p>
          )}
          {errors.otp && (!otpSent || phoneVerified) && (
            <div className="phone-number-input-error-msg">{errors.otp}</div>
          )}
        </div>
        </div>

        <FormControl
          fullWidth
          error={errors['password'] || errors.passwordValidateError}
          className="signup-text-input"
          variant="outlined"
        >
          <InputLabel htmlFor="outlined-adornment-password">
            Password
          </InputLabel>
          <OutlinedInput
            value={registerData.password || ""}
            onChange={handleRegistrationData}
            name="password"
            sx={{ borderRadius: 2 }}
            id="outlined-adornment-password"
            type={showPassword1 ? "text" : "password"}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  onMouseUp={handleMouseUpPassword}
                  edge="end"
                >
                  {showPassword1 ? <MdVisibilityOff /> : <MdVisibility />}
                </IconButton>
              </InputAdornment>
            }
            label="Password"
          />
        </FormControl>
        <div className="password_validate_message_box">
          <p className={passwordValidate.upperLetter ?
            "password_validate_message active" :
            (errors['password']) ? "password_validate_message error" : "password_validate_message"}>

            <IoMdCheckmarkCircleOutline className="password_validate_log" />
            Require at least 1 Uppercase.
          </p>
          <p className={passwordValidate.lowerLetter ?
            "password_validate_message active" :
            (errors['password']) ? "password_validate_message error" : "password_validate_message"}>

            <IoMdCheckmarkCircleOutline className="password_validate_log" />
            Require at least 1 lowercase.
          </p>
          <p className={passwordValidate.specialSymbal ?
            "password_validate_message active" :
            (errors['password']) ? "password_validate_message error" : "password_validate_message"}>
            <IoMdCheckmarkCircleOutline className="password_validate_log" />
            Must include exactly one of: ! @ $ * (no other special characters).
          </p>
          <p className={passwordValidate.allowedChars ?
            "password_validate_message active" :
            (errors['password']) ? "password_validate_message error" : "password_validate_message"}>
            <IoMdCheckmarkCircleOutline className="password_validate_log" />
            No other characters allowed.
          </p>
          <p className={passwordValidate.number ?
            "password_validate_message active" :
            (errors['password']) ? "password_validate_message error" : "password_validate_message"}>
            <IoMdCheckmarkCircleOutline className="password_validate_log" />
            Require at least 1 Number.
          </p>
          <p className={passwordValidate.length ?
            "password_validate_message active" :
            (errors['password']) ? "password_validate_message error" : "password_validate_message"}>
            <IoMdCheckmarkCircleOutline className="password_validate_log" />
            Minimum 8 and Maximum 20 characters.
          </p>
          
        </div>
        <FormControl
          fullWidth
          className="signup-text-input"
          error={errors['confirmPassword'] || errors.passwordValidateError}
          variant="outlined"
        >
          <InputLabel htmlFor="outlined-adornment-password2">
            Confirm Password
          </InputLabel>
          <OutlinedInput
            sx={{ borderRadius: 2 }}
            value={registerData.confirmPassword || ""}
            onChange={handleRegistrationData}
            name="confirmPassword"
            id="outlined-adornment-password2"
            type={showPassword2 ? "text" : "password"}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowConfirmPassword}
                  onMouseDown={handleMouseDownPassword}
                  onMouseUp={handleMouseUpPassword}
                  edge="end"
                >
                  {showPassword2 ? <MdVisibilityOff /> : <MdVisibility />}
                </IconButton>
              </InputAdornment>
            }
            label="Confirm Password"
          />
          {errors['confirmPassword'] && (
            <FormHelperText sx={{ color: "red" }}>
              {errors['confirmPassword']}
            </FormHelperText>
          )}
        </FormControl>
        <div className="registation_date_and_game">
          {/* <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              className="date_picker"
              value={registerData.dob}
              onChange={(e) => handleRegistrationData({ target: { name: 'dob', value: e } })}
              label="Date of Birth"
              slotProps={
                errors['dob'] && {
                  textField: {
                    color: "secondary",
                    error: "true",
                    helperText: "Select your date of birth",
                  },
                }
              }
              sx={{ width: "100%" }}
            />
          </LocalizationProvider> */}
          <FormControl sx={{ width: '100%' }}
            className="signup-text-input"
            error={errors['game']}
          >

            <InputLabel id="demo-multiple-chip-label">Game</InputLabel>
            <Select
              labelId="demo-multiple-chip-label"
              id="demo-multiple-chip"
              value={registerData.game || ''}
              onChange={handleRegistrationData}
              label='Game'
              name="game"
            >
              {gameNames?.map((name) => (
                <MenuItem
                  key={name.gameId}
                  value={name.gameId}
                >
                  {name.gameName}
                </MenuItem>
              ))}
            </Select>
            {errors['game'] && (
              <FormHelperText sx={{ color: "red" }}>
                {errors['game']}
              </FormHelperText>
            )}
          </FormControl>
        </div>
        {/* <CustomPlaceAutocomplete
          onChange={(val) => {
            handleRegistrationData({ target: { name: 'address', value: val } })
          }}
          error={errors['address']}
          helperText={errors['address'] && errors['address']}
          value={registerData?.address || ''}
          variant="outlined"
          label="Address" /> */}
        <FormControl>
          <TextareaAutosize
            minRows={4}
            maxLength={1000}
            name="description"
            value={registerData.description || ""}
            onChange={handleRegistrationData}
            placeholder="Description"
            className={
              errors.description
                ? "club-register-textarea error"
                : "club-register-textarea"
            }
          />
          {errors['description'] && (
            <FormHelperText sx={{ color: "red" }}>
              {errors['description']}
            </FormHelperText>
          )}
        </FormControl>
        <ReCAPTCHA
          style={{ marginTop: '20px' }}
          sitekey={import.meta.env.VITE_APP_CAPTCHA_SITE_KEY}
          onChange={(data) => verifyCaptcha(data)}
        />
        <div className="player_reg_btn">
          <Button disabled={!captchaVerified} type="submit" variant="contained">
            Free trial(30 days)
          </Button>
        </div>
        <div className="club_privacy_policy">
          <p>By signing up, you agree to the Sportmingle <Link to={'https://sportmingle.com/terms_and_conditions.html'}>Term of Services</Link> and <Link to={'https://sportmingle.com/privacy_policy.html'}>Privacy Policy</Link></p>
        </div>
        <div className="redirect_to_sign_in">
          Already have an account?<Link to={'/login'}>Sign in</Link>
        </div>
      </form>
    </div>
  );
};

export default ClubRegister;